import { useEffect, useState, useMemo } from "react";
import api, { fileUrl } from "@/lib/api";
import { fallbackFor, variantLabel } from "@/lib/constants";
import { MapPin } from "lucide-react";

export default function TimelinePage() {
  const [posters, setPosters] = useState([]);

  useEffect(() => {
    api.get("/posters").then((r) => setPosters(r.data));
  }, []); // run once on mount; api + state setter are stable

  const timeline = useMemo(() => {
    return posters
      .filter((p) => p.show_date)
      .sort((a, b) => (a.show_date < b.show_date ? 1 : -1));
  }, [posters]);

  const fmt = (d) => {
    try {
      return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch {
      return d;
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-4xl">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-acid">Show History</div>
        <h1 className="font-display text-5xl lg:text-6xl text-paper leading-none mt-1">TIMELINE</h1>
        <div className="font-mono text-xs text-muted-gray mt-2">{timeline.length} dated shows</div>
      </div>

      {timeline.length === 0 ? (
        <div className="border border-dashed border-stroke p-12 text-center font-mono text-sm text-muted-gray">
          Add a show date to your posters to build your concert timeline.
        </div>
      ) : (
        <div className="relative pl-8" data-testid="concert-timeline">
          <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-stroke" />
          {timeline.map((p, i) => (
            <div key={p.id} className="relative mb-8 animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
              <div className="absolute -left-[29px] top-1.5 w-4 h-4 rounded-full bg-acid border-2 border-ink shadow-[0_0_12px_rgba(0,255,102,0.6)]" />
              <div className="font-mono text-xs text-acid mb-2">{fmt(p.show_date)}</div>
              <div className="bg-surface border border-stroke p-4 flex gap-4 hard-shadow">
                <img
                  src={p.image_paths?.length ? fileUrl(p.image_paths[0]) : fallbackFor(p.category, i)}
                  alt="" className="w-16 h-20 object-cover border border-stroke shrink-0" />
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-gray">{p.band}</div>
                  <div className="font-display text-2xl text-paper leading-tight">{p.title}</div>
                  <div className="flex items-center gap-2 mt-1 font-mono text-[11px] text-muted-gray">
                    {(p.venue_name || p.venue_city) && (
                      <span className="flex items-center gap-1">
                        <MapPin size={11} /> {[p.venue_name, p.venue_city].filter(Boolean).join(", ")}
                      </span>
                    )}
                    <span className="text-cyan">{variantLabel(p.variant)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
